import { useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector }  from 'react-redux'
import {
  Box, Chip, ClickAwayListener, Dialog, DialogContent,
  IconButton, Paper,
  Tooltip, Typography,
} from '@mui/material'
import CloseIcon         from '@mui/icons-material/Close'
import FilterAltOffIcon  from '@mui/icons-material/FilterAltOff'
import SearchIcon        from '@mui/icons-material/Search'
import SelectAllIcon     from '@mui/icons-material/SelectAll'
import DeselectIcon      from '@mui/icons-material/Deselect'
import DownloadIcon      from '@mui/icons-material/Download'
import { Line }          from 'react-chartjs-2'
import { TextField, InputAdornment } from '@mui/material'
import { buildDatasets, buildOptions, TECH_BADGE, getSeriesColor } from './LockdownKPIDashboardChartConfig'
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png'
import { setSelectedEntities } from '../../reducers/filtersReducer'

const TECH_BORDER = { '5G': '#00897b', '4G': '#1565c0', '3G': '#e65100', '2G': '#ad1457' }

function cloneForChart(series) { return JSON.parse(JSON.stringify(series)) }

export default function LockdownKPIDashboardChartModal({ open, onClose, tech, kpiName, chart, showMarks }) {
  const dispatch         = useDispatch()
  const selectedEntitiesMap = useSelector(s => s.filters.selectedEntities)
  const selectedEntities    = selectedEntitiesMap[tech] ?? []
  const chartRef         = useRef(null)

  const [search,        setSearch]        = useState('')
  const [legendOpen,    setLegendOpen]    = useState(true)
  const [tooltip,       setTooltip]       = useState(null)
  const [tooltipLocked, setTooltipLocked] = useState(false)
  const lockedRef     = useRef(false)
  const tooltipRef    = useRef(null)
  const justPinnedRef = useRef(false)
  const chartBoxRef   = useRef(null)
  lockedRef.current  = tooltipLocked
  tooltipRef.current = tooltip

  const badge = TECH_BADGE[tech] ?? TECH_BADGE['4G']
  const unit  = chart?.unit ?? chart?.Unit ?? ''

  const allEntities = useMemo(() => {
    const series = chart?.series ?? chart?.Series ?? []
    return series.map((s, i) => [s.entityId ?? s.EntityId ?? '', i])
  }, [chart])

  const filtered = useMemo(() =>
    search.trim()
      ? allEntities.filter(([id]) => id.toLowerCase().includes(search.toLowerCase()))
      : allEntities
  , [allEntities, search])

  const hasSelection   = selectedEntities.length > 0
  const allFilteredSel = filtered.every(([id]) => selectedEntities.includes(id)) && filtered.length > 0

  const handleToggleAll = () => {
    const ids = filtered.map(([id]) => id)
    if (allFilteredSel) dispatch(setSelectedEntities({ tech, entities: selectedEntities.filter(id => !ids.includes(id)) }))
    else dispatch(setSelectedEntities({ tech, entities: [...new Set([...selectedEntities, ...ids])] }))
  }

  const { chartData, options } = useMemo(() => {
    if (!chart?.series?.length) return {}
    const plainSeries = cloneForChart(chart.series)
    const { datasets, labels } = buildDatasets(plainSeries, selectedEntities)
    // Lockdown has no phases — pass empty array regardless of showMarks
    const opts = buildOptions([], labels, unit)
    opts.tooltips = {
      enabled: false, mode: 'index', intersect: false,
      custom: (model) => {
        if (lockedRef.current) return
        if (model.opacity === 0) { setTooltip(null); return }
        if (!model.dataPoints?.length) return
        const chartWidth = chartBoxRef.current?.offsetWidth ?? 800
        setTooltip({
          date:   model.dataPoints[0]?.xLabel ?? '',
          x:      model.caretX,
          side:   model.caretX > chartWidth / 2 ? 'left' : 'right',
          points: model.dataPoints.map(p => ({
            label: datasets[p.datasetIndex]?.label ?? '',
            idx:   p.datasetIndex,
            value: p.yLabel,
          })),
        })
      },
    }
    return { chartData: { labels, datasets }, options: opts }
  }, [chart, selectedEntities, showMarks, unit])

  const handleExport = async () => {
    const html2canvas = (await import('html2canvas')).default
    const el = chartRef.current
    if (!el) return
    const canvas = await html2canvas(el, { scale: 3, useCORS: true, backgroundColor: '#ffffff', allowTaint: true })
    const link    = document.createElement('a')
    link.download = `${kpiName}_${tech}.png`
    link.href     = canvas.toDataURL('image/png')
    link.click()
  }

  if (!open) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { width: '90vw', height: '85vh', borderRadius: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, borderTop: `3px solid ${TECH_BORDER[tech]}` }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
          {kpiName}
        </Typography>
        {unit && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{unit}</Typography>}
        <Chip label={tech} size="small" sx={{ bgcolor: badge.bg, color: badge.color, border: `1px solid ${badge.border}`, fontWeight: 700, fontSize: 9, height: 18 }} />
        <Tooltip title="Export as PNG">
          <IconButton size="small" onClick={handleExport} sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box
          ref={chartBoxRef}
          onMouseLeave={() => { if (!lockedRef.current) setTooltip(null) }}
          onMouseDown={() => { if (tooltipRef.current && !lockedRef.current) { lockedRef.current = true; justPinnedRef.current = true; setTooltipLocked(true) } }}
          sx={{ flex: 1, minWidth: 0, p: 2, position: 'relative' }}
        >
          <Box ref={chartRef} sx={{ height: '100%', position: 'relative' }}>
            {chartData?.labels?.length
              ? <>
                  <Line key={chartData.datasets.length} data={chartData} options={{ ...options, maintainAspectRatio: false }} />

                  <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 2, width: '30%' }}>
                    <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%', opacity: 0.05 }} />
                  </Box>

                  {tooltip && (
                    <ClickAwayListener onClickAway={() => { if (justPinnedRef.current) { justPinnedRef.current = false; return } setTooltipLocked(false); setTooltip(null) }}>
                      <Box sx={{
                        position: 'absolute', top: 8,
                        ...(tooltip.side === 'right' ? { left: tooltip.x + 12 } : { right: `calc(100% - ${tooltip.x}px + 12px)` }),
                        zIndex: 20, pointerEvents: tooltipLocked ? 'auto' : 'none', width: 200,
                      }}>
                        <Paper elevation={4} sx={{ border: '1.5px solid', borderColor: tooltipLocked ? 'primary.main' : '#d0d7e3', borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 300 }}>
                          <Box sx={{ px: 1.5, py: '6px', background: tooltipLocked ? 'linear-gradient(90deg, #1565c0, #1976d2)' : 'linear-gradient(90deg, #e3f2fd, #f0f7ff)', borderBottom: '1px solid', borderColor: tooltipLocked ? '#1976d2' : '#d0e8f8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 700, color: tooltipLocked ? '#fff' : 'primary.dark' }}>{tooltip.date}</Typography>
                            <Typography sx={{ fontSize: 8, fontWeight: 700, color: tooltipLocked ? 'rgba(255,255,255,0.8)' : 'text.disabled' }}>{tooltipLocked ? '📌 PINNED' : 'click to pin'}</Typography>
                          </Box>
                          <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                            {tooltip.points.map((p, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: 1.5, py: '4px', borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' } }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: getSeriesColor(p.label, p.idx), flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>{p.label || 'Cluster'}</Typography>
                                <Typography sx={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', fontWeight: 700, color: 'text.primary', flexShrink: 0 }}>{typeof p.value === 'number' ? `${p.value.toFixed(3)}${unit ? ' '+unit : ''}` : '—'}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      </Box>
                    </ClickAwayListener>
                  )}
                </>
              : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.disabled">No data</Typography>
                </Box>
            }
          </Box>
        </Box>

        <Box sx={{ width: legendOpen ? 200 : 28, minWidth: legendOpen ? 200 : 28, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', transition: 'width .2s, min-width .2s', overflow: 'hidden' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: legendOpen ? 'space-between' : 'center', px: legendOpen ? 1 : 0, py: '5px', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, gap: 0.5 }}>
            {legendOpen && (
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>
                {allEntities.length} series
              </Typography>
            )}
            {legendOpen && hasSelection && (
              <Tooltip title="Clear selection">
                <IconButton size="small" onClick={() => dispatch(setSelectedEntities({ tech, entities: [] }))} sx={{ p: '2px' }}>
                  <FilterAltOffIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
            )}
            {legendOpen && (
              <Tooltip title={allFilteredSel ? 'Deselect visible' : 'Select visible'}>
                <IconButton size="small" onClick={handleToggleAll} sx={{ p: '2px' }}>
                  {allFilteredSel ? <DeselectIcon sx={{ fontSize: 14 }} /> : <SelectAllIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setLegendOpen(v => !v)} sx={{ p: '2px' }}>
              <Typography sx={{ fontSize: 12 }}>{legendOpen ? '›' : '‹'}</Typography>
            </IconButton>
          </Box>

          {legendOpen && (
            <>
              <Box sx={{ px: 1, py: '5px', borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <TextField size="small" fullWidth placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { height: 24, fontSize: 11, borderRadius: 1.5 }, '& .MuiOutlinedInput-input': { py: 0, px: '4px' } }}
                />
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', py: '2px' }}>
                {filtered.map(([id, idx]) => {
                  const color      = getSeriesColor(id, idx)
                  const isSelected = selectedEntities.includes(id)
                  return (
                    <Box key={id} onClick={() => {
                      const next = isSelected
                        ? selectedEntities.filter(e => e !== id)
                        : [...selectedEntities, id]
                      dispatch(setSelectedEntities({ tech, entities: next }))
                    }} sx={{
                      display: 'flex', alignItems: 'center', gap: '7px',
                      px: 1.5, py: '3px', cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.light' : 'transparent',
                      '&:hover': { bgcolor: isSelected ? '#bbdefb' : 'action.hover' },
                    }}>
                      <Box sx={{ width: 18, height: 4, borderRadius: 2, bgcolor: color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: isSelected ? 'primary.dark' : 'text.secondary', fontWeight: isSelected ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {id || 'Cluster'}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
